package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.decodeURIComponent
import com.siriusxm.pia.components.ServiceViewContext
import com.siriusxm.pia.components.serviceContentView
import com.siriusxm.pia.views.unifiedaggregator.events.EventFilters
import com.siriusxm.pia.views.unifiedaggregator.management.BaseTypesEditor
import com.siriusxm.pia.views.unifiedaggregator.management.ProducerCreateCapability
import com.siriusxm.pia.views.unifiedaggregator.management.PartialsEditor
import com.siriusxm.pia.views.unifiedaggregator.management.RelationshipPermissionsEditor
import com.siriusxm.pia.views.unifiedaggregator.management.capabilityRoute
import com.siriusxm.pia.views.unifiedaggregator.management.producerEditor
import com.siriusxm.pia.views.unifiedaggregator.management.producerUpdateCapability
import com.siriusxm.pia.views.unifiedaggregator.management.producers

/**
 * Typed navigation for the producer subtree of the aggregator app (UCA-1028).
 *
 * This is a pilot of a typed-destination-key design, scoped deliberately to producers. The rest of
 * the aggregator app continues to route through the nested `switch` / `select` DSL in
 * `AggregatorRouting.kt`; the two styles coexist, with [ProducerRoutes] mounted under the single
 * ordinary `select("producers")` that already existed.
 *
 * The design is Nav3-inspired but keeps only the parts that mean something in a browser:
 *
 *  - **No back stack.** `window.history` already is the back stack. The URL is the single source of
 *    truth and the live destination is a pure function of it ([parseProducerPath]). A parallel
 *    in-memory stack would only be a second thing to keep in sync.
 *  - **No scenes, decorators, per-entry saved state, or transition metadata.** None of those have an
 *    analogue in Compose HTML.
 *
 * What remains is the part that carries the weight: a typed key ([ProducerKey]), a total function
 * from URL to key ([parseProducerPath]), a total function from key back to URL ([NavKey.location]), and a
 * flat `when` that renders each key ([ProducerRoutes]).
 *
 * Nothing here touches `cmc-core` or `cmc-ui`. Both are published to Artifactory and consumed by the
 * EPG app, so public API added there becomes another team's maintenance burden.
 */

/**
 * The application root all aggregator destinations live under.
 *
 * The one statement of the mount point, read by [NavKey.href]. Keys themselves are
 * application-relative and no longer each declare it.
 */
internal const val AGGREGATOR_ROOT = "aggregator"

/** The path segment, relative to the aggregator root, that owns the producer subtree. */
private const val PRODUCERS = "producers"

/**
 * The segment used both for "create a new producer" (`producers/edit`) and "edit this producer"
 * (`producers/{id}/edit`). Kept as one constant because the URL grammar uses one word for both.
 */
private const val EDIT = "edit"

private const val BASE_TYPES = "base-types"
private const val PARTIALS = "partials"
private const val RELATIONSHIP_PERMISSIONS = "relationship-permissions"

/**
 * The tabs on the producer detail page.
 *
 * [id] is deliberately both the `tabView` tab id and the URL segment, so there is exactly one string
 * per tab in the codebase. Callers pass a `ProducerTab` rather than a `String`, which is what makes a
 * mistyped tab a compile error instead of a tab bar with nothing selected.
 */
enum class ProducerTab(val id: String) {
    Permissions("permissions"),
    Events("events"),
    Errors("errors"),
    History("history");

    companion object {
        /** The tab shown when the URL names no tab, or names one we don't recognise. */
        val Default: ProducerTab = Permissions

        /** Resolve a URL segment to a tab, or null if it isn't one of ours. */
        fun fromId(id: String?): ProducerTab? = entries.find { it.id == id }
    }
}

/**
 * Every URL-addressable destination in the producer subtree.
 *
 * This is the "single place that says what a destination is" that the string-based DSL lacks: adding
 * a destination means adding a variant here, which makes both [subtreeSegments] and [ProducerRoutes]
 * fail to compile until they handle it — so a new destination cannot exist without both a URL and a
 * renderer. Those two `when`s are the only enumerations of this family, and both are compiler-checked;
 * there is no hand-maintained list to fall out of sync.
 */
sealed interface ProducerKey : NavKey {
    /**
     * This destination's segments *within* the producer subtree — raw, not percent-encoded.
     *
     * This is the one function a new destination has to implement, and it deliberately deals in raw
     * values: encoding is applied once, for every family at once, in [RouteLocation.toPath]. An author adding a
     * destination therefore cannot forget to encode an id, because encoding is not theirs to write.
     * That is the actual mechanism behind the claim in [NavKey] — a `when` branch returning a
     * hand-built string could still forget it.
     *
     * [ProducerDetail] always emits its tab, so every shared link is explicit about which tab it
     * means. Omitting the tab still parses to [ProducerTab.Default], so older links keep working.
     *
     * The `when` is exhaustive over the sealed type, so a new destination cannot be added without
     * giving it a URL.
     *
     * Note what is *not* here: none of these variants carries filter state. Filters are query
     * parameters, and this function is about path segments only — see `EventFilters` and
     * `plans/2026-09-01-UCA-1028-producer-filter-url-state.md` §3 for why they were kept off the keys.
     */
    fun subtreeSegments(): List<String> = when (this) {
        ProducersList -> emptyList()
        NewProducer -> listOf(EDIT)
        is ProducerDetail -> listOf(id, tab.id)
        is ProducerEdit -> listOf(id, EDIT)
        is ProducerBaseTypes -> listOf(id, BASE_TYPES)
        is ProducerPartials -> listOf(id, PARTIALS)
        is ProducerRelationshipPermissions -> listOf(id, RELATIONSHIP_PERMISSIONS)
    }

    /**
     * This destination as an aggregator-relative location, its segments still raw.
     *
     * Encoding is not here — it is in [RouteLocation.toPath], for every family at once. It matters
     * because real producer ids contain slashes and colons (`sxm/mddb-bus/mddb-event-bus` becomes
     * `sxm%2Fmddb-bus%2Fmddb-event-bus`); previously each of the fifteen call sites that built a
     * producer URL had to remember that independently, and this family's job is now only to say which
     * raw segments it means.
     *
     * ### Round-trip law
     *
     * `parseProducerPath(key.subtreeSegments().map { encodeURIComponent(it) }) == key`
     *
     * for any key an admin can reach. The `map` is not incidental: the mount point hands
     * [parseProducerPath] the *encoded* remainder of the URL, which is why that function decodes each
     * segment before matching. Encoding raw segments and then decoding them is an identity, so the
     * law holds even for an id that itself contains a `%`.
     *
     * The location carries no query. Query state belongs to the caller and is joined on at
     * navigation time by `AggregatorService.navigate(key, query)`; a key that pinned its own query
     * would make every link to a destination also an assertion about its filters.
     *
     * The leading `producers` segment is added here rather than in [subtreeSegments] because
     * `navigate` and `href` both want the full aggregator-relative path, while the parser only ever
     * sees what follows the `select("producers")` mount point.
     */
    override fun location(): RouteLocation = RouteLocation(listOf(PRODUCERS) + subtreeSegments())
}

/** The producers list. With no producer selected, the list shows the aggregate dashboard. */
data object ProducersList : ProducerKey

/**
 * A single producer's detail page, on a specific [tab].
 *
 * [id] is the raw (unencoded) producer id. An id that no longer resolves to a producer is not an
 * error: the detail renderer falls back to the unselected list, matching the previous DSL.
 */
data class ProducerDetail(val id: String, val tab: ProducerTab = ProducerTab.Default) : ProducerKey

/*
 * The five producer-management destinations.
 *
 * These keys carry navigation state, not entitlement. [ProducerRoutes] checks the corresponding AVP
 * create/update action before it renders an editor, and the catalog API independently enforces the same
 * action. Keeping authorization out of the URL decoder lets an AVP-authorized caller use these routes
 * without relying on the legacy Cognito admin flag.
 */

/** The editor for creating a producer. */
data object NewProducer : ProducerKey

/** The editor for an existing producer. */
data class ProducerEdit(val id: String) : ProducerKey

/** The dedicated base-types permission editor. */
data class ProducerBaseTypes(val id: String) : ProducerKey

/** The dedicated partials permission editor. */
data class ProducerPartials(val id: String) : ProducerKey

/** The dedicated relationship-permissions editor. */
data class ProducerRelationshipPermissions(val id: String) : ProducerKey

/**
 * The base path of a producer's detail page, aggregator-relative and with the id encoded.
 *
 * Kept separate from [NavKey.location] because `tabView`'s `tabNavigation` takes a base URL and
 * appends `/{tabId}` itself, so it needs the detail path *without* a tab. It goes through the same
 * [RouteLocation.toPath] as every other producer URL, so there is still exactly one encode step.
 *
 * Note that a tab switch through `tabNavigation` therefore drops the query — which is the behaviour
 * we want: the Events and Errors tabs do not share a filter vocabulary, so carrying `search` onto the
 * Events tab or `entityType` onto the Errors tab would put parameters in the URL that nothing reads
 * and nothing can clear.
 */
internal fun producerDetailBasePath(id: String): String =
    RouteLocation(listOf(PRODUCERS, id)).toPath()

/**
 * Decode a producer URL to the destination it names. Pure, and total: every input yields a key.
 *
 * [segments] is the `/`-separated remainder of the path after `producers`, still percent-encoded.
 * Each segment is decoded before it is matched, mirroring `cmc-core`'s `DefaultRoute.component`
 * (which is also decoded, so `%65dit` matches `edit` there and here).
 *
 * Authorization is intentionally not an input. The decoder returns the destination named by the URL;
 * [ProducerRoutes] owns the asynchronous AVP check and does not render protected content until it is
 * allowed. The catalog API remains the security boundary.
 *
 * ### Fall-through rules
 *
 * - No segments, or only blank ones: [ProducersList].
 * - A leading `edit` segment is checked *before* it could be read as a producer id. A producer whose
 *   id is literally `edit` is therefore still unreachable, as before.
 * - A second segment that is neither a management action nor a known tab id resolves to
 *   [ProducerTab.Default]. This covers an unknown tab from a stale link or hand-edited typo. It also
 *   sidesteps a `tabView` rough edge — `tabView` only falls back to the first tab when the
 *   selection is null, so an unrecognised id would otherwise render a tab bar with nothing selected
 *   and no content.
 * - Segments beyond the second are ignored, as the DSL ignored them by simply not looking.
 */
fun parseProducerPath(segments: List<String>): ProducerKey {
    val path = segments.map { decodeURIComponent(it) }.filter { it.isNotBlank() }

    val head = path.firstOrNull() ?: return ProducersList
    if (head == EDIT) {
        return NewProducer
    }

    val action = path.getOrNull(1)
    when (action) {
        EDIT -> return ProducerEdit(head)
        BASE_TYPES -> return ProducerBaseTypes(head)
        PARTIALS -> return ProducerPartials(head)
        RELATIONSHIP_PERMISSIONS -> return ProducerRelationshipPermissions(head)
    }

    return ProducerDetail(head, ProducerTab.fromId(action) ?: ProducerTab.Default)
}

/**
 * Route the producer subtree: decode [path] to a [ProducerKey] and render it.
 *
 * [path] is the route's `remainingPath` at the `select("producers")` mount point — everything after
 * `producers/`, still percent-encoded and without the query string. [query] is that query string,
 * already parsed; the mount point has to supply it separately because `cmc-core` splits path from
 * query at the top of the tree and `remainingPath` never carries one.
 *
 * The `when` is exhaustive over [ProducerKey], so adding a destination is a compile error here until
 * it is handled. Because matching has already happened by the time we get here, this is a flat list
 * of render sites rather than the ten-level interleaving of matching and rendering it replaces.
 *
 * Each `when` branch is a distinct Compose call site, which reproduces the previous behaviour where
 * each `select` branch was its own call site: moving between a detail page and an editor discards the
 * other's state, while moving between tabs of the same producer stays on the single
 * [ProducerDetail] call site and merely passes a different tab.
 *
 * Only [ProducerDetail] reads [query] — its Events and Errors tabs keep their filter selections
 * there. Every other destination ignores it, so a stray parameter on an editor URL is inert.
 */
@Composable
fun ServiceViewContext.ProducerRoutes(
    aggregator: AggregatorService,
    path: String,
    query: Query = Query.Empty
) {
    when (val key = parseProducerPath(path.split("/"))) {
        ProducersList -> producers(aggregator, null)

        // `producerById` returning null is not an error: an id that names no producer renders the
        // list with nothing selected, which is what `producers(context, producer?.id)` did before.
        is ProducerDetail -> producers(
            aggregator,
            aggregator.producerById(key.id)?.id,
            key.tab,
            EventFilters.from(query)
        )

        NewProducer -> serviceContentView({}) {
            aggregator.context.capabilityRoute(
                ProducerCreateCapability,
                operation = "create producers"
            ) {
                producerEditor(aggregator)
            }
        }

        // Do not turn an unknown id into a create form: update authorization is scoped to the id in
        // the URL, and creation has a separate action.
        is ProducerEdit -> serviceContentView({}) {
            aggregator.context.capabilityRoute(
                producerUpdateCapability(key.id),
                operation = "update this producer"
            ) {
                aggregator.producerById(key.id)?.let { producerEditor(aggregator, it) }
            }
        }

        is ProducerBaseTypes -> serviceContentView({}) {
            aggregator.context.capabilityRoute(
                producerUpdateCapability(key.id),
                operation = "update this producer"
            ) {
                aggregator.producerById(key.id)?.let { BaseTypesEditor(aggregator, it) }
            }
        }

        is ProducerPartials -> serviceContentView({}) {
            aggregator.context.capabilityRoute(
                producerUpdateCapability(key.id),
                operation = "update this producer"
            ) {
                aggregator.producerById(key.id)?.let { PartialsEditor(aggregator, it) }
            }
        }

        is ProducerRelationshipPermissions -> serviceContentView({}) {
            aggregator.context.capabilityRoute(
                producerUpdateCapability(key.id),
                operation = "update this producer"
            ) {
                aggregator.producerById(key.id)?.let { RelationshipPermissionsEditor(aggregator, it) }
            }
        }
    }
}
